import { getMockDb } from "@/mock-api/db.ts";
import type { LocationSpecificData } from "@/mock-api/defaultDb.ts";
import { addResponseToError, ErrorWithResponse } from "@/mock-api/msw/responseError.ts";
import type { StandardRequest } from "@/mock-api/msw/StandardRequest.ts";
import { standardErrorResponse } from "@/mock-api/responses.ts";
import { mustGetOwn } from "@warrenio/utils/collections/getOwnProperty";
import { notNull } from "@warrenio/utils/notNull";
import { HttpResponse } from "msw";
import { first } from "remeda";
import invariant from "tiny-invariant";
import type z from "zod";
import { zfd } from "zod-form-data";

const SIMULATE_LOCATION_ERROR = false;

//#region Standard mock data generators

export function getUserId(r: StandardRequest) {
    return getCurrentUser(r).id;
}

/** @public */
export function getUserEmail(r: StandardRequest) {
    return notNull(getCurrentUser(r).profile_data.email);
}

function getCurrentUser(r: StandardRequest) {
    const _apikey = r.request.headers.get("apikey");
    // TODO: support different mock users
    return notNull(first(getMockDb().users));
}

export function generateId() {
    return getMockDb().nextId++;
}

/** Generate a deterministic pseudo-UUID */
export function generateUuid() {
    const baseId = generateId();
    const hexId = baseId.toString(16).padStart(8, "0");
    return `${hexId}-0000-4000-8000-000000000000`;
}

//#endregion

//#region Location accessors

export function getLocationParam(r: StandardRequest) {
    return getParam(r, "location");
}

export function getLocationDb(request: StandardRequest): LocationSpecificData {
    const location = getLocationParam(request);
    if (SIMULATE_LOCATION_ERROR && location === "jhv") {
        throw new ErrorWithResponse(
            HttpResponse.text(`Simulated server error for location ${location}`, { status: 500 }),
        );
    }
    return mustGetOwn(getMockDb().locationsData, location, "location");
}

//#endregion

//#region Request accessors

export function getParam(r: StandardRequest, name: string) {
    const value = mustGetOwn(r.params, name, "params");
    invariant(typeof value === "string", `Expected param ${name} to be a string`);
    return value;
}

export function getUuidParam(r: StandardRequest) {
    return getParam(r, "uuid");
}

export async function parseForm<T extends z.ZodTypeAny>(r: StandardRequest, schema: T): Promise<z.output<T>> {
    const entries = await getFormEntries(r);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return -- ESLint bug?
    return zfd.formData(schema).parse(entries);
}

function raiseParseError(r: StandardRequest, cause: unknown, notContentType: string) {
    throw new Error(
        `Failed to parse body data for request ${r.request.url}\nIs the content type right (eg. not ${notContentType})?`,
        { cause },
    );
}

async function getFormEntries(r: StandardRequest) {
    try {
        const entries = [...(await r.request.formData()).entries()];
        logEntries(entries);
        return entries;
    } catch (e) {
        raiseParseError(r, e, "JSON");
    }
}

export async function parseJson<T extends z.ZodTypeAny>(r: StandardRequest, schema: T): Promise<z.output<T>> {
    const entries = await getJsonEntries(r);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return -- ESLint bug?
    return zfd.formData(schema).parse(entries);
}

export async function parseBodyParams<T extends z.ZodTypeAny>(r: StandardRequest, schema: T) {
    const contentType = r.request.headers.get("Content-Type") ?? "";
    if (contentType.startsWith("application/json")) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return -- ESLint bug?
        return await parseJson(r, schema);
    } else if (
        contentType.startsWith("application/x-www-form-urlencoded") ||
        contentType.startsWith("multipart/form-data")
    ) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return -- ESLint bug?
        return await parseForm(r, schema);
    } else {
        throw new Error(`Unsupported content type for request ${r.request.url}: ${contentType}`);
    }
}

async function getJsonEntries(r: StandardRequest) {
    try {
        const entries = Object.entries((await r.request.json()) as object);
        logEntries(entries);
        return entries;
    } catch (e) {
        raiseParseError(r, e, "form");
    }
}

function logEntries(entries: [string, unknown][]) {
    console.debug("Form entries: %s", entries.map(([k, v]) => `${JSON.stringify(k)}: ${JSON.stringify(v)}`).join(", "));
}

export function getQueryParams(r: StandardRequest): URLSearchParams {
    const url = new URL(r.request.url);
    return url.searchParams;
}

//#endregion

//#region Standard responses

export function validateSpec<T extends z.ZodTypeAny>(spec: T, obj: z.infer<T>) {
    try {
        spec.parse(obj);
    } catch (e) {
        throwWithStandardErrorResponse(e, `Validation error: ${e}`);
    }
}

export function throwStandardErrorResponse(errorMessage: string): never {
    throw new ErrorWithResponse(standardErrorResponse(errorMessage));
}

function throwWithStandardErrorResponse(e: unknown, errorMessage?: string): never {
    if (e instanceof Error) {
        addResponseToError(e, standardErrorResponse(errorMessage ?? e.message));
    }
    throw e;
}

//#endregion
